'use es6';

import PortalIdParser from 'PortalIdParser';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { stringify as stringifyParams } from 'hub-http/helpers/params';
import { parseUrl } from 'hub-http/helpers/url';
import * as CategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/CategorySlug';
import * as SuperCategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/SuperCategorySlug';
import { isQa, deployed } from './env';
import { getCategorySuperCategory } from 'ecosystem-marketplace-core/utils/groupTags';
import { getFullHref } from './history';
const APP_ROOT = '/marketplace/apps';
export const getLoginUrl = () => `${getFullUrl('app', {
  hubletOverride: na1
})}/login`;
export const getAppRootUrl = () => `/ecosystem/${PortalIdParser.get()}${APP_ROOT}`;
export const getAdminPreviewRootUrl = () => '/marketplace';

const getLocation = () => window ? window.location : {};

export const getAppShortlink = (pathname, params = {}) => {
  return `${getFullUrl('app', {
    hubletOverride: na1
  })}/l/ecosystem${APP_ROOT}${pathname}${Object.keys(params).length ? `?${stringifyParams(params)}` : ''}`;
};
export const getLoginUrlWithRedirect = pathname => `${getLoginUrl()}/?loginRedirectUrl=${encodeURIComponent(`${getAppShortlink(pathname)}`)}`;

const getLocalizedUrlPath = (lang = 'en', external = false) => {
  switch (lang) {
    case 'pt':
    case 'pt-br':
      return external ? '/br' : '/pt';

    case 'de':
    case 'es':
    case 'fr':
    case 'nl':
    case 'it':
      return `/${lang}`;

    case 'ja':
      return external ? '/jp' : '/ja';

    default:
      return '';
  }
};

export const getPublicRootUrl = (lang = 'en') => {
  return `${getLocalizedUrlPath(lang)}${APP_ROOT}`;
};
export const getLocalizedPublicPath = (lang, pathname = '') => {
  return `${getPublicRootUrl(lang)}${pathname}`;
};
export const getLocalizedPublicUrl = (lang, pathname) => {
  const {
    origin = getFullUrl('ecosystem', {
      hubletOverride: na1
    })
  } = getLocation();
  return `${origin}${getLocalizedPublicPath(lang, pathname)}`;
};
export const getFormattedUrl = href => {
  if (!href) return null;
  const {
    protocol
  } = parseUrl(href);
  return !protocol ? `http://${href}` : href;
};
export const getSuperCategoryFilterUrl = superCategory => {
  const superCategoryFacet = SuperCategorySlug[superCategory];

  if (!superCategoryFacet) {
    console.error(`Unable to lookup facet for super category '${superCategory}'`);
    return '';
  }

  return `/${superCategoryFacet}`;
};
export const getCategoryFilterUrl = (category, _superCategory) => {
  const categoryFacet = CategorySlug[category];

  if (!categoryFacet) {
    console.error(`Unable to lookup facet for category '${category}'`);
    return '';
  }

  const superCategory = _superCategory || getCategorySuperCategory(category);

  return `${getSuperCategoryFilterUrl(superCategory)}/${categoryFacet}`;
};
export const getListingDetailUrl = (slug, category, superCategory) => {
  return `${getCategoryFilterUrl(category, superCategory)}/${slug}`;
};
const PAGE_PATH_REGEX = /\/page\/\d+/;
export const stripPathPagination = pathname => {
  return pathname.replace(PAGE_PATH_REGEX, '');
};
export const getPathPagination = (pathname, page) => {
  if (page <= 1) {
    return stripPathPagination(pathname);
  }

  const paginationPath = `/page/${page}`;
  return PAGE_PATH_REGEX.test(pathname) ? pathname.replace(PAGE_PATH_REGEX, paginationPath) : `${pathname}${paginationPath}`;
};
export const getHasPathPagination = pathname => {
  return PAGE_PATH_REGEX.test(pathname);
};
export const getConnectedAppsUrl = isCustomer => {
  if (!isCustomer) {
    return `${getFullUrl('app', {
      hubletOverride: na1
    })}/l/integrations-settings/installed`;
  }

  return `${getFullUrl('app')}/integrations-settings/${PortalIdParser.get()}/installed`;
};
export const getAllCollectionsUrl = () => '/';
export const getAllCategoriesUrl = () => '/all-categories';
export const getStorefrontScreenshotUrl = () => `https://${deployed() ? 'static' : 'local'}.hsappstatic.net/ecosystem-marketplace-apps-public-ui/ex/img/storefront-screenshot${isQa() ? '-qa' : ''}.png`; // TODO add nl and it once supported

export const getLocalizedHubSpotUrl = (lang, subdomain = 'www') => {
  switch (lang) {
    case 'de':
    case 'es':
    case 'fr':
      return `https://${subdomain}.hubspot.${lang}`;

    case 'ja':
      return `https://${subdomain}.hubspot.jp`;

    case 'pt':
      return 'https://br.hubspot.com';

    default:
      return `https://${subdomain}.hubspot.com`;
  }
};
export const getSignupUrl = (lang, pathname) => {
  const url = `${getLocalizedHubSpotUrl(lang)}/products/get-started`;

  if (pathname) {
    const referrer = getFullHref(pathname, false);
    return `${url}?hubs_content=${referrer}`;
  }

  return url;
};
export const getContactUrl = lang => {
  return `${getLocalizedHubSpotUrl(lang, 'offers')}/contact-sales`;
};
export const getAllReviewsUrl = (isPublic, slug, category, superCategory) => {
  const ROOT = isPublic ? getPublicRootUrl() : getAppRootUrl();
  return `${ROOT}${getListingDetailUrl(slug, category, superCategory)}/reviews`;
};
const tierGroupPricingPath = {
  CRM: '/crm',
  MARKETING: '/marketing',
  SALES: '/sales',
  SERVICE: '/service',
  CMS: '/cms',
  OPERATIONS: '/operations',
  OPS: '/operations' // Remove once backend changes plans to use "OPERATIONS_" prefix

};
export const getPricingUrl = (isCustomer, hub) => {
  const path = hub ? tierGroupPricingPath[hub] : '';

  if (!isCustomer) {
    return `https://www.hubspot.com/pricing${path}`;
  }

  return `${window.location.origin}/pricing/${PortalIdParser.get()}${path}`;
};
export const getHubSpotPrivacyUrl = (lang = 'en') => `https://legal.hubspot.com${getLocalizedUrlPath(lang, true)}/privacy-policy`;
export const getHubSpotTermsOfServiceUrl = (lang = 'en') => `https://legal.hubspot.com${getLocalizedUrlPath(lang, true)}/terms-of-service`;
export const getHubSpotAcceptableUseUrl = (lang = 'en') => `https://legal.hubspot.com${getLocalizedUrlPath(lang, true)}/acceptable-use`;
export const getDataSyncProductUrl = (lang = 'en') => `${getLocalizedHubSpotUrl(lang)}/products/data-sync`;